import type { ComponentType } from 'react'
import { Link } from '@tanstack/react-router'
import { FaMap as BussboardIcon, FaDrum as WerkshopIcon } from 'react-icons/fa'
import { useNavStore } from '@/stores/navStore'

/**
 * Pill-group switcher between the two top-level apps —
 * **Bussboard** (the node-graph signal-flow canvas, formerly
 * Layouts / Studios) and **Werkshop** (the DAW). Rendered inside
 * `AppHeader`'s right-hand slot on workspace pages; hidden on
 * marketing/landing surfaces.
 *
 * Werkshop sub-surfaces (Sequencer, MIDI, Tools, Store, Settings)
 * are navigated via the in-Werkshop `WerkshopShell` tab strip, not
 * this switcher. Keep this strip short so users aren't confused
 * between "apps" and "sections".
 */

export type WorkspaceKey = 'bussboard' | 'werkshop'

interface WorkspaceItem {
  key: WorkspaceKey
  to: '/bussboard' | '/werkshop'
  label: string
  Icon: ComponentType<{ size?: number; className?: string }>
  title: string
}

const WORKSPACES: readonly WorkspaceItem[] = [
  {
    key: 'bussboard',
    to: '/bussboard',
    label: 'Bussboard',
    Icon: BussboardIcon,
    title:
      'Bussboard — map any signal-flow rig: studio, live stage, broadcast truck, camera + lighting',
  },
  {
    key: 'werkshop',
    to: '/werkshop',
    label: 'Werkshop',
    Icon: WerkshopIcon,
    title: 'Werkshop — sequencer, MIDI router, tools, and module store',
  },
] as const

interface WorkspaceSwitcherProps {
  /** Which workspace is currently active — highlighted in the strip. */
  active: WorkspaceKey
}

export function WorkspaceSwitcher({ active }: WorkspaceSwitcherProps) {
  // Last-seen URL per workspace category — lets the pills route
  // back to the user's editing context (e.g. `/bussboard/<id>`
  // rather than `/bussboard`). Falls back to the category root
  // when no visit has been recorded yet.
  const lastSeen = useNavStore(s => s.lastSeen)
  return (
    <nav
      aria-label="Switch workspace"
      className="inline-flex items-center gap-0.5 rounded-lg bg-surface-sunken p-0.5 border border-brd-subtle"
    >
      {WORKSPACES.map(ws => {
        const isActive = ws.key === active
        const deepLink = lastSeen[ws.key]
        // Only substitute the deep link when it's non-empty AND
        // we're NOT already on the workspace (otherwise clicking
        // the active pill would just re-route to itself, hiding
        // the "click me to reset" affordance). The Link's `to`
        // prop is typed to the category roots, so deep links use
        // the low-level `href`.
        const href = deepLink && !isActive ? deepLink : ws.to
        const useDeep = href !== ws.to
        return useDeep ? (
          <a
            key={ws.key}
            href={href}
            title={ws.title}
            className="px-2.5 sm:px-3 py-1 rounded-md text-xs font-medium transition-colors duration-150 flex items-center gap-1.5 text-content-secondary hover:text-content-primary"
          >
            <ws.Icon size={12} className="flex-shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{ws.label}</span>
          </a>
        ) : (
          <Link
            key={ws.key}
            to={ws.to}
            title={ws.title}
            aria-current={isActive ? 'page' : undefined}
            className={`px-2.5 sm:px-3 py-1 rounded-md text-xs font-medium transition-colors duration-150 flex items-center gap-1.5 ${
              isActive
                ? 'bg-surface-raised text-content-primary shadow-low'
                : 'text-content-secondary hover:text-content-primary'
            }`}
          >
            <ws.Icon size={12} className="flex-shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{ws.label}</span>
          </Link>
        )
      })}
    </nav>
  )
}
